import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Modal, Form, Row, Col, Upload, message
} from 'antd';
import { AntdButton, InputField, SelectInput } from '../../../blueplanitUtils/InputField';
import { setLoader } from '../../../Slices/loaderSlice';
import { updateAsset } from '../../../Slices/assetSlice';
import { handleMultipleAttachment } from '../../../blueplanitUtils/attachments';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload } from '@fortawesome/free-solid-svg-icons';

const AddWarrantyModal = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const [form] = Form.useForm();
    const [selectedId, setSelectedId] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [selectedAssetAttachments, setSelectedAssetAttachments] = useState([]);
    const [newWarrantyAttachments, setNewWarrantyAttachments] = useState([]);

    const details = useSelector((state) => state.employee.AllEmployees);
    const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
    const AllAssets = useSelector((state) => state.Assets.allAssets);
    const { Empid } = useParams();

    const id = loginUser?.id;
    const ids = Empid || id;
    const dispatch = useDispatch();

    const updateWarrantyFromSelection = (selectedAsset) => {
        if (selectedAsset && selectedAsset.id) {
            setSelectedId(selectedAsset.id);
            form.setFieldsValue({
                warrantyDuration: selectedAsset.warrantyDuration ?? null,
            });
            setSelectedAssetAttachments(selectedAsset.warrantyAttachments || []);
            setNewWarrantyAttachments([]);
        }
    };

    const handleAddModalChange = ({ fileList }) => {
        const freshAttachments = fileList.map(file => ({
            file: file.originFileObj,
            filename: file.name,
            url: URL.createObjectURL(file.originFileObj),
            type: file.type
        }));

        setAttachments(freshAttachments);
    };


    const handleModalClose = () => {
        form.resetFields();
        setSelectedAssetAttachments([]);
        setNewWarrantyAttachments([]);
        setAttachments([]);
        setSelectedId(null);
        onClose();
    };

    const handleSubmit = () => {
        form.validateFields().then(async (values) => {
            dispatch(setLoader(true));
            try {
                if (!selectedId) {
                    message.error('Please select an asset first');
                    return;
                }
                const warrantyDurationValue = values.warrantyDuration;

                let uploaded = [];
                if (attachments && attachments.length > 0) {
                    uploaded = await handleMultipleAttachment(attachments, details, dispatch, loginUser?.id, ids);
                }

                await dispatch(updateAsset({
                    id: selectedId,
                    warrantyDuration: parseInt(warrantyDurationValue),
                    editedBy: `${loginUser?.firstname} ${loginUser?.lastname}`,
                    editedAt: new Date().toISOString(),
                    warrantyAttachments: uploaded,
                })).unwrap();

                message.success('Warranty updated successfully!');
                handleModalClose();
                if (onSuccess) {
                    onSuccess();
                }
            } catch (error) {
                message.error('Failed to update warranty');
            } finally {
                dispatch(setLoader(false));
            }
        }).catch((error) => {
            message.error('Please fill all required fields correctly.');
        });
    };

    return (
        <Modal
            title="Add Warranty"
            open={isOpen}
            onCancel={handleModalClose}
            onOk={handleSubmit}
            okText="Add"
            width={500}
        >
            <Form layout="vertical" form={form}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="assetTagId"
                            required={false}
                            className="tight-error-spacing"
                            rules={[{ required: true, message: 'Please enter Asset Tag ID' }]}
                            label={
                                <span>
                                    Asset Tag ID<span style={{ color: 'red', marginLeft: 4 }}>*</span>
                                </span>
                            }
                        >
                            <SelectInput
                                placeholder="Select Asset Tag ID"
                                showSearch
                                optionFilterProp="children"
                                onChange={(selectedId) => {
                                    const selectedAsset = AllAssets?.records?.find(asset => asset.id === selectedId);
                                    updateWarrantyFromSelection(selectedAsset);
                                }}
                                options={
                                    AllAssets?.records
                                        ?.filter(asset => {
                                            const warranty = parseFloat(asset.warrantyDuration);
                                            return isNaN(warranty) || warranty === 0;
                                        })
                                        ?.map(asset => ({
                                            label: `${asset.assetTagId} - ${asset.productName}`,
                                            value: asset.id,
                                        }))
                                }
                            />

                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="warrantyDuration"
                            required={false}
                            label={
                                <span>
                                    Warranty Duration (Months)<span style={{ color: 'red', marginLeft: 4 }}>*</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter warranty duration' },
                                {
                                    type: 'number',
                                    min: 1,
                                    transform: (value) => {
                                        if (value && !isNaN(value)) {
                                            return Number(value);
                                        }
                                        return value;
                                    },
                                    message: 'Warranty must be at least 1 month'
                                }
                            ]}
                        >
                            <InputField
                                type="number"
                                min={1}
                                placeholder={selectedId ? "Enter warranty duration in months" : "Please select an asset first"}
                                disabled={!selectedId}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            name="warrantyAttachments"
                            label="Warranty Attachments"
                        >
                            <div>
                                {selectedAssetAttachments.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                            {selectedAssetAttachments.map((file, index) => (
                                                <div key={index} style={{
                                                    padding: '4px 8px',
                                                    backgroundColor: '#f0f0f0',
                                                    borderRadius: '4px',
                                                    fontSize: '12px'
                                                }}>
                                                    <a
                                                        href={file?.url || file?.data}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {file.filename || `Attachment ${index + 1}`}
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {attachments.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                            {attachments?.map((file, index) => (
                                                <div key={index} style={{
                                                    padding: '4px 8px',
                                                    backgroundColor: '#e6f7ff',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    <span>{file.filename}</span>
                                                    <FontAwesomeIcon icon={faTimes}
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: '#ff4d4f',
                                                            fontSize: '10px'
                                                        }}
                                                        onClick={() => {
                                                            setAttachments(prev => prev.filter((_, i) => i !== index));
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <Upload
                                    multiple
                                    showUploadList={false}
                                    beforeUpload={() => false}
                                    onChange={handleAddModalChange}
                                    fileList={[]}
                                    disabled={!selectedId}
                                >
                                    <AntdButton
                                        label=" New Warranty"
                                        icon={<FontAwesomeIcon icon={faUpload} />}
                                        type="primary"
                                        disabled={!selectedId}
                                        style={{
                                            marginTop: '5px',
                                            borderRadius: '10px',
                                            fontWeight: 500,
                                        }}
                                    />


                                </Upload>

                                {(selectedAssetAttachments.length === 0 && newWarrantyAttachments.length === 0) && (
                                    <span style={{ color: '#999', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                                        No warranty attachments available. Upload new documents above.
                                    </span>
                                )}
                            </div>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default AddWarrantyModal;
